import Details from '@/components/Details/Details';
import InfoLabel from '@/components/InfoLabel';
import IEventType from '@/types/IEventType';
import IThirdPartyCalendar from '@/types/IThirdPartyCalendar';
import { IEventTypeWrite } from '@/types/apiClient/IEventTypeWrite';
import { Checkbox, FormControl, FormErrorMessage, FormHelperText, FormLabel, Input, Select } from '@chakra-ui/react';
import { FcAbout } from 'react-icons/fc';
import CRSelect from 'react-select';
import CalendarOption from './components/CalendarOption';
import LocationOption from './components/LocationOption';
import useSectionGeneral from './useSectionGeneral';

export interface ISectionGeneralProps {
	service: IEventType;
	onChange: (values: Partial<IEventTypeWrite>) => void;
}

export default function SectionGeneral(props: ISectionGeneralProps) {
	const {
		onSubmit,
		registerLocationField,
		registerSubmitOnBlur,
		registerSubmitOnChange,
		handlePrimaryCalendarChange,
		t,
		errors,
		isLoading,
		isPhysicalLocationFieldVisible,
		locationOptions,
		shouldAutoComputeStep,
		values,
		calendars,
		isPrimaryCalendarSelectorHidden,
	} = useSectionGeneral(props);

	return (
		<Details image={<FcAbout />} title={t('General Information')}>
			<form onSubmit={onSubmit}>
				<FormControl mb="1rem" isInvalid={!!errors.name}>
					<InfoLabel
						htmlFor="name"
						text={t('Reservation calendar name')}
						help={t('The name will be displayed on the booking page.')}
					/>
					<Input id="name" {...registerSubmitOnBlur('name')} isDisabled={isLoading} />
					<FormErrorMessage>{errors.name?.message}</FormErrorMessage>
				</FormControl>

				<FormControl mb="1rem" isInvalid={!!errors.description}>
					<InfoLabel
						htmlFor="description"
						text={t('Reservation calendar description')}
						help={t('The description will be displayed on the booking page below the name.')}
					/>
					<Input id="description" {...registerSubmitOnBlur('description')} isDisabled={isLoading} />
					<FormErrorMessage>{errors.description?.message}</FormErrorMessage>
				</FormControl>

				<FormControl mb="1rem" id="duration">
					<InfoLabel htmlFor="duration" text={t('Duration')} help={t('Choose the event duration.')} />
					<Select {...registerSubmitOnChange('duration')} isDisabled={isLoading}>
						<option value="PT10M">10m</option>
						<option value="PT15M">15m</option>
						<option value="PT20M">20m</option>
						<option value="PT30M">30m</option>
						<option value="PT45M">45m</option>
						<option value="PT1H">1h</option>
						<option value="PT1H15M">1h 15m</option>
						<option value="PT1H30M">1h 30m</option>
						<option value="PT2H">2h</option>
						<option value="PT3H">3h</option>
						<option value="PT4H">4h</option>
						<option value="PT5H">5h</option>
					</Select>
				</FormControl>

				{isPrimaryCalendarSelectorHidden ? null : (
					<FormControl mt={4}>
						<InfoLabel
							text={t('Primary calendar')}
							help={t('The primary calendar is the calendar where the event will be created.')}
							htmlFor="primary-calendar"
						/>
						<CRSelect
							isDisabled={isLoading}
							isClearable
							value={
								(calendars.find(
									(c) => c.id === props.service.thirdPartyCalendars.toSyncBookings.at(0)?.id
								) as IThirdPartyCalendar) || undefined
							}
							onChange={handlePrimaryCalendarChange}
							options={calendars}
							components={{ Option: CalendarOption }}
						/>
					</FormControl>
				)}

				<FormControl mt="4">
					<InfoLabel text={t('Location')} help={t('Configure the meeting location')} htmlFor="location" />

					<CRSelect
						isDisabled={isLoading}
						className="chakra-select"
						inputId="location"
						defaultValue={locationOptions.find((option) => option.value === values.location)}
						{...registerLocationField()}
						options={locationOptions}
						components={{ Option: LocationOption }}
					/>

					{values.location === 'BOOKER_SELECTION' ? (
						<FormHelperText> {t('Edit available options on the location question.')} </FormHelperText>
					) : null}

					{values.location === 'BOOKER_PHONE' ? (
						<FormHelperText> {t('A phone question was added to the booking page.')} </FormHelperText>
					) : null}
				</FormControl>

				{isPhysicalLocationFieldVisible ? (
					<FormControl mt={4} isInvalid={!!errors.locationMeta}>
						<FormLabel htmlFor="locationMeta">{t('Custom location')}</FormLabel>
						<Input
							isDisabled={isLoading}
							autoFocus
							id="locationMeta"
							{...registerSubmitOnBlur('locationMeta')}
							placeholder={t('Write custom location details here')}
						/>
						<FormErrorMessage> {errors.locationMeta?.message} </FormErrorMessage>
					</FormControl>
				) : null}

				<FormControl mt={4} mb="1rem">
					<FormLabel htmlFor="step">{t('Start time frequency')}</FormLabel>
					<Select
						isDisabled={isLoading}
						id="step"
						{...registerSubmitOnChange('step')}
						display={shouldAutoComputeStep ? 'none' : 'initial'}
					>
						<option value="PT5M">5m</option>
						<option value="PT10M">10m</option>
						<option value="PT15M">15m</option>
						<option value="PT20M">20m</option>
						<option value="PT30M">30m</option>
						<option value="PT1H">1h</option>
					</Select>
					<FormHelperText>
						<Checkbox {...registerSubmitOnChange('shouldAutoComputeStep')} isDisabled={isLoading} size="sm">
							{t('Calculate from duration')}
						</Checkbox>
					</FormHelperText>
				</FormControl>
			</form>
		</Details>
	);
}
